import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChatStreamEvent, streamChat } from 'src/api/streamChat';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { queryKeys } from 'src/api/queries/queryKeys';
import {
  IChatUiMessage,
  LOCAL_MESSAGE_ID_PREFIX,
  hasServerId,
  mergeMessages,
  messageText,
  messagesBefore,
  useConversationMessagesQuery,
} from 'src/api/queries/chat/useChatConversationQueries';
import { applyStreamEvent } from 'src/app/chat/applyStreamEvent';
import { getErrorMessage, logger } from 'src/utils';

export interface ChatError {
  message: string;
  code?: string;
  retryAfterSeconds?: number;
}

export interface ISendMessageOptions {
  /** The latest user message with a server id; it and every later message are replaced by this send. */
  replaceFromMessageId?: string;
}

export interface IUseChatStreamOptions {
  conversationId: string | null;
  /** Resolves the conversation the send goes to, creating it on the first send of a new chat. */
  ensureConversation: () => Promise<string>;
  isForkPending: () => boolean;
  /** The app path the user sends from, passed to the server as page context. */
  page: string;
  thinkHarder: boolean;
  refreshUsage: () => Promise<void>;
}

export interface IChatStream {
  messages: IChatUiMessage[];
  loadingConversation: boolean;
  streaming: boolean;
  error: ChatError | null;
  sendMessage: (text: string, options?: ISendMessageOptions) => Promise<void>;
  regenerate: () => void;
  retryLast: () => void;
  stop: () => void;
  /** Aborts the reply in flight and drops the overlay, error and retry text, before the view moves to another conversation. */
  resetStream: () => void;
  isSending: () => boolean;
  getMessages: () => IChatUiMessage[];
}

/** @registry The open chat conversation's message thread: cached history merged with the streaming overlay, plus send, stop, edit-and-resend, regenerate and retry; used by `ChatProvider`. */
export const useChatStream = ({
  conversationId,
  ensureConversation,
  isForkPending,
  page,
  thinkHarder,
  refreshUsage,
}: IUseChatStreamOptions): IChatStream => {
  const queryClient = useQueryClient();

  // Locally-managed streaming overlay: the just-sent user message plus the
  // in-flight assistant reply, merged on top of the cached history so the
  // exchange never disappears while streaming or while the conversation id
  // transitions from null (new chat) to the server-assigned id.
  const [overlayMessages, setOverlayMessages] = useState<IChatUiMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);
  // While a replacing send waits for its 'start' event, the history from this
  // message on is hidden; it comes back if the send fails before 'start'.
  const [replaceCutoffId, setReplaceCutoffId] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const sendingRef = useRef(false);
  const lastMessageRef = useRef<string | null>(null);

  // Fetching (not the query key) is suspended while streaming, so a
  // background refetch of history can't race the in-flight message — but the
  // key stays stable on `conversationId`, so already-cached history keeps
  // rendering underneath the streaming overlay instead of disappearing and
  // flashing back once the stream ends.
  const messagesQuery = useConversationMessagesQuery(conversationId, {
    enabled: !streaming,
  });
  // `isLoading` only reflects an actual in-flight first fetch (status
  // 'pending' + fetchStatus 'fetching') — a disabled query with no cached
  // data is 'pending' but fetchStatus 'idle', so `isLoading` is false. This
  // is already false for a brand-new (null) conversation and stays false
  // while streaming suspends fetching, without needing extra guards.
  const loadingConversation = messagesQuery.isLoading;
  const messages = useMemo(() => {
    const history = messagesQuery.data ?? [];
    return mergeMessages(
      replaceCutoffId ? messagesBefore(history, replaceCutoffId) : history,
      overlayMessages,
    );
  }, [messagesQuery.data, overlayMessages, replaceCutoffId]);
  // Read from callbacks that must keep a stable identity across streamed
  // tokens, so the memoised message bubbles are not re-rendered on each one.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const overlayRef = useRef(overlayMessages);
  overlayRef.current = overlayMessages;

  const getMessages = useCallback(() => messagesRef.current, []);
  const isSending = useCallback(() => sendingRef.current, []);

  const resetStream = useCallback(() => {
    abortControllerRef.current?.abort();
    lastMessageRef.current = null;
    setOverlayMessages([]);
    setReplaceCutoffId(null);
    setStreaming(false);
    setError(null);
  }, []);

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    setStreaming(false);
  }, []);

  const sendMessage = useCallback(
    async (text: string, options?: ISendMessageOptions) => {
      const trimmed = text.trim();
      if (!trimmed || sendingRef.current || isForkPending()) return;
      sendingRef.current = true;
      lastMessageRef.current = trimmed;
      const replaceFromMessageId = options?.replaceFromMessageId;

      setError(null);
      const localUserMessageId = `${LOCAL_MESSAGE_ID_PREFIX}user-${crypto.randomUUID()}`;
      const userMessage: IChatUiMessage = {
        id: localUserMessageId,
        role: 'user',
        segments: [{ kind: 'text', text: trimmed }],
        createdAt: new Date(),
      };
      const assistantMessageId = `${LOCAL_MESSAGE_ID_PREFIX}assistant-${crypto.randomUUID()}`;
      const assistantMessage: IChatUiMessage = {
        id: assistantMessageId,
        role: 'assistant',
        segments: [],
        streaming: true,
        createdAt: new Date(),
        thoughtHarder: thinkHarder,
      };
      // SSE events arrive sequentially, so this closure variable is always
      // current for the happy-path cache fold below. Failure paths (catch
      // block, SSE 'error' event) deliberately do NOT write to it; they use
      // a functional setOverlayMessages update instead (see
      // `markAssistantSettled`), so a cancelled stream can't resurrect an
      // overlay that resetStream already cleared out from under it in the
      // meantime.
      let exchange: IChatUiMessage[] = [userMessage, assistantMessage];
      let hadServerError = false;
      let started = false;
      const previousOverlay = overlayRef.current;
      setOverlayMessages(exchange);
      if (replaceFromMessageId) setReplaceCutoffId(replaceFromMessageId);
      setStreaming(true);

      // The server writes 'start' right after it saves the replace. A
      // replacing send that ends before the client sees 'start' shows the
      // hidden messages again and refetches them, because an abort or a
      // dropped connection can land after that save.
      const failedBeforeReplacing = () => !!replaceFromMessageId && !started;
      const restoreReplacedMessages = (sentTo: string | null) => {
        setReplaceCutoffId(null);
        setOverlayMessages((prev) =>
          prev.some((m) => m.id === localUserMessageId)
            ? previousOverlay
            : prev,
        );
        if (sentTo) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.chat.messages(sentTo),
          });
        }
      };

      const markAssistantSettled = (
        update: (message: IChatUiMessage) => IChatUiMessage,
      ) => {
        setOverlayMessages((prev) =>
          prev.map((m) => (m.id === assistantMessageId ? update(m) : m)),
        );
      };

      const controller = new AbortController();
      abortControllerRef.current = controller;
      let sentToConversationId: string | null = null;

      try {
        const id = await ensureConversation();
        sentToConversationId = id;

        await streamChat({
          conversationId: id,
          message: trimmed,
          page,
          replaceFromMessageId,
          thinkHarder,
          signal: controller.signal,
          onEvent: (event: ChatStreamEvent) => {
            const next = applyStreamEvent(exchange, event);
            if (next !== exchange) {
              exchange = next;
              setOverlayMessages(exchange);
            }
            switch (event.type) {
              case 'start':
                started = true;
                if (replaceFromMessageId) {
                  queryClient.setQueryData<IChatUiMessage[]>(
                    queryKeys.chat.messages(id),
                    (existing) =>
                      existing &&
                      messagesBefore(existing, replaceFromMessageId),
                  );
                  setReplaceCutoffId(null);
                }
                break;
              case 'memory':
                invalidateFor('chat.memory.write', queryClient);
                break;
              case 'done':
                // A reply that used notes changed their use counts.
                if (event.usedMemoryIds.length) {
                  invalidateFor('chat.memory.write', queryClient);
                }
                break;
              case 'error':
                hadServerError = true;
                setError({
                  message: event.message,
                  code: event.code,
                  retryAfterSeconds: event.retryAfterSeconds,
                });
                if (!failedBeforeReplacing()) {
                  markAssistantSettled((m) => ({
                    ...m,
                    streaming: false,
                    interruption: 'failed',
                  }));
                }
                break;
              // refreshUsage() after the stream ends is the authoritative
              // source for usage (it includes server-computed budget fields).
              case 'usage':
              case 'delta':
              case 'tool_use':
              case 'tool_result':
              case 'chart':
              case 'table':
                break;
              default:
                logger.warn('Ignoring an unknown chat stream event', {
                  type: (event as { type: string }).type,
                });
            }
          },
        });

        // A server-sent 'error' event doesn't throw — streamChat still
        // resolves normally once the stream closes — so guard the fold on
        // it explicitly. The failed exchange stays in the overlay only
        // (visible until the user starts a new conversation or retries):
        // the history cache must match what a refetch returns, and the
        // server either saved nothing or saved a question and an error reply
        // that Retry replaces.
        if (failedBeforeReplacing()) {
          restoreReplacedMessages(id);
        } else if (!hadServerError) {
          // Fold the finished exchange into the cached history for this
          // conversation (seeding the cache if this is the first message of
          // a brand-new conversation), then clear the overlay now that it's
          // captured there.
          queryClient.setQueryData<IChatUiMessage[]>(
            queryKeys.chat.messages(id),
            (existing) => [...(existing ?? []), ...exchange],
          );
          setOverlayMessages([]);
        }
        queryClient.invalidateQueries({
          queryKey: queryKeys.chat.conversations(),
        });
        refreshUsage();
      } catch (err) {
        const aborted = (err as Error)?.name === 'AbortError';
        if (!aborted) {
          logger.error('Chat message failed', { err });
          setError({ message: getErrorMessage(err) });
        }
        if (failedBeforeReplacing()) {
          restoreReplacedMessages(sentToConversationId);
        } else if (aborted) {
          markAssistantSettled((m) => ({
            ...m,
            streaming: false,
            interruption: 'stopped',
          }));
        } else {
          markAssistantSettled((m) => ({
            ...m,
            streaming: false,
            interruption: 'failed',
          }));
        }
      } finally {
        setStreaming(false);
        abortControllerRef.current = null;
        sendingRef.current = false;
      }
    },
    [
      ensureConversation,
      isForkPending,
      refreshUsage,
      page,
      queryClient,
      thinkHarder,
    ],
  );

  // Replaces the last question when the server holds it, so a resend never
  // stores the same question twice.
  const resendInPlaceOfLastQuestion = useCallback(
    (textFor: (lastQuestion: IChatUiMessage | undefined) => string | null) => {
      const lastQuestion = messagesRef.current.findLast(
        (m) => m.role === 'user',
      );
      const text = textFor(lastQuestion);
      if (!text) return;
      void sendMessage(
        text,
        lastQuestion && hasServerId(lastQuestion)
          ? { replaceFromMessageId: lastQuestion.id }
          : undefined,
      );
    },
    [sendMessage],
  );

  const regenerate = useCallback(
    () =>
      resendInPlaceOfLastQuestion((lastQuestion) =>
        lastQuestion ? messageText(lastQuestion) : null,
      ),
    [resendInPlaceOfLastQuestion],
  );

  const retryLast = useCallback(
    () =>
      resendInPlaceOfLastQuestion(
        (lastQuestion) =>
          lastMessageRef.current ??
          (lastQuestion ? messageText(lastQuestion) : null),
      ),
    [resendInPlaceOfLastQuestion],
  );

  return {
    messages,
    loadingConversation,
    streaming,
    error,
    sendMessage,
    regenerate,
    retryLast,
    stop,
    resetStream,
    isSending,
    getMessages,
  };
};
